import { isEmpty } from 'lodash';
import React, { useEffect, useMemo } from 'react';
import {
  ChipFilters,
  CommonButton,
  EmptyStateView,
  EndListView,
  ListSkeleton,
  PullRefreshContainer,
  SpinnerLoader,
  searchComponents,
} from '../../components';
import { usePaymentList } from '../hooks';
import { SVGS } from '../../assets';
import { PaymentCard } from '../payment-card';
import './accept-payment-screen-content.scss';
import {
  FIELD_OPS_ROUTES,
  PaymentStatusEnum,
  SearchConfig,
  SELLER_WEBAPP_ROUTES,
} from '../../constants';
import {
  APP_TYPE,
  getAppType,
  getSelectedItemIds,
  getSendType,
  Payment,
  useAppSelector,
} from '@zonofi/zono-money-store';
import { useNavigate } from 'react-router-dom';
import { ChipFilterSection } from '../../types';
import { getTextFromRange } from '../..';

interface AccpetPaymentScreenProps {
  setPaymentData?: React.Dispatch<React.SetStateAction<Payment[]>>;
  searchConfig?: SearchConfig;
  setShowSearch?: (val: boolean) => void;
  showSearch?: boolean;
  onDateChipPress?: () => void;
  isCustomer?: boolean;
}

export const AcceptPaymentScreenContent: React.FC<AccpetPaymentScreenProps> = ({
  setPaymentData = () => {},
  searchConfig = {} as SearchConfig,
  setShowSearch = () => {},
  showSearch = false,
  onDateChipPress = () => {},
  isCustomer = false,
}) => {
  const {
    paymentsData,
    onScroll,
    lastItemRef,
    isLoading,
    onPaymentCardPress,
    isEndOfList,
    statusFilters,
    setAppliedStatus,
    appliedDateRange,
    refreshPaymentList,
  } = usePaymentList({ isCustomer });

  const navigate = useNavigate();
  const appState = useAppSelector(getAppType);
  const sendType = useAppSelector(getSendType);
  const selectedPaymentIds = useAppSelector(getSelectedItemIds);

  const searchProp =
    appState === APP_TYPE.FIELDS_OPS
      ? { search: searchComponents({ searchConfig, setShowSearch }) }
      : undefined;

  useEffect(() => {
    setPaymentData(paymentsData ?? []);
  }, [paymentsData]);

  const onContinuePress = () => {
    const routeToNavigate =
      appState === APP_TYPE.SELLER_HUB
        ? SELLER_WEBAPP_ROUTES
        : FIELD_OPS_ROUTES;
    navigate(routeToNavigate.VIEW_SELECTED_PAYMENTS);
  };

  const chips: ChipFilterSection[] = useMemo(
    () => [
      {
        items: [
          {
            id: '',
            value: getTextFromRange(appliedDateRange),
            selected: true,
            type: 'date',
          },
        ],
        onItemPress: onDateChipPress,
      },
      {
        items: statusFilters,
        onItemPress: ({ id }: { id: string }) =>
          setAppliedStatus(id ? [id as PaymentStatusEnum] : []),
      },
    ],
    [appliedDateRange, statusFilters]
  );

  return (
    <>
      <div className="payment-list-content">
        <ChipFilters
          sections={chips}
          chipFilterStyles={{
            marginTop: '-12px',
            alignSelf: 'flex-start',
            width: '100%',
            position: 'sticky',
            top: '-16px',
            left: '-16px',
            ...(appState === APP_TYPE.FIELDS_OPS &&
              searchConfig?.enabled && { padding: 0 }),
          }}
          {...(searchProp && searchProp)}
          showSearch={showSearch}
        />
        <PullRefreshContainer
          className="payment-list-content-container"
          onScroll={onScroll}
          onRefresh={refreshPaymentList}
        >
          {isLoading ? (
            <ListSkeleton className="payment-list-screen-skeleton" />
          ) : isEmpty(paymentsData) ? (
            <EmptyStateView
              imageSrc={SVGS.NoPaymentIcon}
              message={'No Payments Found'}
            />
          ) : (
            <div className="payment-list-view">
              {paymentsData?.map((payment) => (
                <PaymentCard
                  key={payment.id}
                  item={payment}
                  onCardPress={onPaymentCardPress}
                  showSelection={sendType?.length !== 0}
                />
              ))}
              <div ref={lastItemRef} />
              {!isEndOfList && <SpinnerLoader />}
              {isEndOfList && <EndListView />}
            </div>
          )}
        </PullRefreshContainer>
      </div>
      {selectedPaymentIds?.length > 0 && (
        <div className="continue-bottom-button">
          <CommonButton
            text="View Selected Payments"
            onPress={onContinuePress}
          />
        </div>
      )}
    </>
  );
};
